import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { IoMdCloseCircleOutline } from "react-icons/io";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, onValue, getDatabase, update, remove } from "firebase/database";
import { db } from "../../firebase";
import { COLLECTION_NAME, USER_TYPE } from "../../constants/endpoints";
import { toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../Loader/Loader";
import AssignDriverModal from "../AssignDriverModal/AssignDriverModal";
import {
  SEND_DEACTIVATION_NOTIFICATION,
  SEND_REACTIVATION_NOTIFICATION,
  REFUND_REQUEST,
} from "../../constants/endpoints";

Modal.setAppElement("#root");

const DeactivateAccountModal = ({
  isOpen,
  onRequestClose,
  user,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeRide, setActiveRide] = useState(null);
  const [scheduledRides, setScheduledRides] = useState([]);
  const [refundTotal, setRefundTotal] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [refundingRideId, setRefundingRideId] = useState(null);
  const [showRefundConfirmation, setShowRefundConfirmation] = useState(false);
  const [rideToRefund, setRideToRefund] = useState(null);
  const [isAssignDriverModalOpen, setIsAssignDriverModalOpen] = useState(false);
  const [selectedRideForAssignment, setSelectedRideForAssignment] =
    useState(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const isPassenger = user?.role === "passenger";
  const isDriver = ["Rider", "Driver"].includes(user?.role);
  const isDeactivated = user?.accountStatus === "deactivated";

  // Helper function to format location (handle both string and object)
  const formatLocation = (location) => {
    if (!location) return "Unknown";

    // If it's a string, return it
    if (typeof location === "string") return location;

    // If it's an object with latitude and longitude
    if (
      typeof location === "object" &&
      location.latitude &&
      location.longitude
    ) {
      // Return formatted coordinates
      return `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(
        6
      )}`;
    }

    // If it's an object with a name property
    if (typeof location === "object" && location.name) {
      return location.name;
    }

    // If it's an object with an address property
    if (typeof location === "object" && location.address) {
      return location.address;
    }

    return "Location unavailable";
  };

  useEffect(() => {
    if (isOpen && user && !isDeactivated) {
      fetchRideData();
    } else if (isOpen && isDeactivated) {
      setLoading(false);
    }
  }, [isOpen, user]);

  const fetchRideData = async () => {
    setLoading(true);
    try {
      // Check for active rides (in progress)
      const activeRideData = await checkActiveRide();
      setActiveRide(activeRideData);

      // Fetch all rides (both awaiting and scheduled)
      const allRidesData = await fetchAllRides();
      setScheduledRides(allRidesData);

      // Calculate refund total for passengers
      if (isPassenger && allRidesData.length > 0) {
        const total = allRidesData.reduce((sum, ride) => {
          return sum + (parseFloat(ride.price) || 0);
        }, 0);
        setRefundTotal(total);
      }
    } catch (error) {
      console.error("Error fetching ride data:", error);
      toast.error("Failed to load ride information", {
        position: "top-right",
        autoClose: 3000,
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const checkActiveRide = async () => {
    try {
      // Check in Realtime Database for active rides (in progress)
      // Using "staging-rides" same as ActiveRides screen
      const database = getDatabase();
      const ridesRef = ref(database, COLLECTION_NAME.rides);

      return new Promise((resolve) => {
        onValue(
          ridesRef,
          (snapshot) => {
            const ridesData = snapshot.val();
            let activeRide = null;

            if (ridesData) {
              Object.keys(ridesData).forEach((rideId) => {
                const ride = ridesData[rideId];
                const userIdToCheck = isPassenger ? ride.userId : ride.riderId;

                // Check for rides that are actively in progress
                // 1. Rides with status "accepted" and has riderId
                // 2. Rides with status "pending", socketStatus "rideCreated", and isScheduled false (driver has been assigned and ride is starting)
                const isAcceptedRide =
                  ride.status === "accepted" && ride.riderId;
                const isStartingRide =
                  ride.status === "pending" &&
                  ride.socketStatus === "rideCreated" &&
                  !ride.isScheduled;

                if (
                  userIdToCheck === user.id &&
                  (isAcceptedRide || isStartingRide)
                ) {
                  activeRide = {
                    id: rideId,
                    ...ride,
                    pickupAddressRaw: ride.pickupAddress || ride.pickupLocation,
                    destinationAddressRaw:
                      ride.destinationAddress || ride.dropOffLocation,
                    status: ride.status,
                  };
                }
              });
            }

            resolve(activeRide);
          },
          { onlyOnce: true }
        );
      });
    } catch (error) {
      console.error("Error checking active rides:", error);
      return null;
    }
  };

  const fetchAllRides = async () => {
    try {
      // Fetch from both sources, same as ActiveRides screen
      const database = getDatabase();
      const ridesRef = ref(database, COLLECTION_NAME.rides);

      // 1. Fetch from Realtime Database (awaiting rides)
      const realtimeRides = await new Promise((resolve) => {
        onValue(
          ridesRef,
          (snapshot) => {
            const ridesData = snapshot.val();
            const rides = [];

            if (ridesData) {
              Object.keys(ridesData).forEach((key) => {
                const ride = ridesData[key];
                const userIdToCheck = isPassenger ? ride.userId : ride.riderId;

                // Exclude rides that are considered "in progress"
                // (pending with socketStatus rideCreated and not scheduled)
                const isStartingRide =
                  ride.status === "pending" &&
                  ride.socketStatus === "rideCreated" &&
                  !ride.isScheduled;

                // Get awaiting rides (pending or scheduled status)
                // But exclude rides that are actively starting
                if (
                  userIdToCheck === user.id &&
                  (ride.status === "pending" || ride.status === "scheduled") &&
                  !isStartingRide
                ) {
                  let dateObj;
                  if (ride.createdAt && ride.createdAt._seconds) {
                    dateObj = new Date(ride.createdAt._seconds * 1000);
                  } else if (ride.createdAt instanceof Date) {
                    dateObj = ride.createdAt;
                  } else {
                    dateObj = new Date();
                  }

                  rides.push({
                    id: key,
                    ...ride,
                    createdAt: dateObj,
                    pickupAddressRaw: ride.pickupAddress || ride.pickupLocation,
                    destinationAddressRaw:
                      ride.destinationAddress || ride.dropOffLocation,
                    price: parseFloat(ride.price) || 0,
                    status: ride.status,
                    source: "realtime",
                  });
                }
              });
            }

            resolve(rides);
          },
          { onlyOnce: true }
        );
      });

      // 2. Fetch from Firestore (scheduled rides)
      const scheduledRidesRef = collection(db, COLLECTION_NAME.scheduleRides);
      const snapshot = await getDocs(scheduledRidesRef);

      const firestoreRides = [];
      snapshot.forEach((doc) => {
        const rideData = doc.data();

        // Filter for user's rides with pending or scheduled status
        const userIdToCheck = isPassenger ? rideData.userId : rideData.riderId;
        const isUserRide = userIdToCheck === user.id;
        const isPendingOrScheduled = ["pending", "scheduled"].includes(
          rideData.status
        );

        if (isUserRide && isPendingOrScheduled) {
          let dateObj;
          if (rideData.createdAt && rideData.createdAt._seconds) {
            dateObj = new Date(rideData.createdAt._seconds * 1000);
          } else if (rideData.createdAt instanceof Date) {
            dateObj = rideData.createdAt;
          } else {
            dateObj = new Date();
          }

          firestoreRides.push({
            id: doc.id,
            ...rideData,
            createdAt: dateObj,
            pickupAddressRaw: rideData.pickupAddress || rideData.pickupLocation,
            destinationAddressRaw:
              rideData.destinationAddress || rideData.dropOffLocation,
            price: parseFloat(rideData.price) || 0,
            status: rideData.status,
            source: "firestore",
          });
        }
      });

      // 3. Combine both sources
      const allRides = [...realtimeRides, ...firestoreRides];

      // Sort by created time (earliest first)
      allRides.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return a.createdAt - b.createdAt;
        }
        return 0;
      });

      return allRides;
    } catch (error) {
      console.error("Error fetching rides:", error);
      return [];
    }
  };

  const sendNotification = async (endpoint, data) => {
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error("Failed to send notification:", await response.text());
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      // Don't throw error - notification failure shouldn't block deactivation
    }
  };

  const handleDeactivate = async () => {
    if (!acknowledged) {
      toast.warning("Please acknowledge the deactivation", {
        position: "top-right",
        autoClose: 3000,
        transition: Bounce,
      });
      return;
    }

    setProcessing(true);
    try {
      // Update user status to deactivated
      const userRef = doc(db, COLLECTION_NAME.users, user.id);
      await updateDoc(userRef, {
        accountStatus: "deactivated",
        deactivatedAt: new Date(),
      });

      // Cancel scheduled rides for passengers
      if (isPassenger && scheduledRides.length > 0) {
        for (const ride of scheduledRides) {
          const rideRef = doc(db, COLLECTION_NAME.scheduleRides, ride.id);
          await updateDoc(rideRef, {
            status: "cancelled",
            cancellationReason: "cancelled_due_to_user_deactivation",
            cancelledAt: new Date(),
          });
        }
      }

      // For drivers, scheduled rides will be manually reassigned by admin
      // So we just mark them as needing reassignment
      if (isDriver && scheduledRides.length > 0) {
        for (const ride of scheduledRides) {
          const rideRef = doc(db, COLLECTION_NAME.scheduleRides, ride.id);
          await updateDoc(rideRef, {
            needsReassignment: true,
            previousDriverId: user.id,
            riderId: null, // Remove the driver assignment
          });
        }
      }

      // Send FCM notification to user
      await sendNotification(SEND_DEACTIVATION_NOTIFICATION, {
        userId: user.id,
        userRole: isPassenger ? USER_TYPE.PASSENGER : USER_TYPE.DRIVER,
        userName: user.fullName,
        scheduledRidesCount: scheduledRides.length,
        refundAmount: isPassenger ? refundTotal : 0,
      });

      toast.success(
        `${isPassenger ? USER_TYPE.PASSENGER : USER_TYPE.DRIVER
        } account deactivated successfully!`,
        {
          position: "top-right",
          autoClose: 3000,
          transition: Bounce,
        }
      );

      if (onSuccess) onSuccess();
      onRequestClose();
    } catch (error) {
      console.error("Error deactivating account:", error);
      toast.error("Failed to deactivate account. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        transition: Bounce,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setProcessing(true);
    try {
      const userRef = doc(db, COLLECTION_NAME.users, user.id);
      await updateDoc(userRef, {
        accountStatus: "active",
        reactivatedAt: new Date(),
      });

      // Send FCM notification to user
      await sendNotification(SEND_REACTIVATION_NOTIFICATION, {
        userId: user.id,
        userRole: isPassenger ? USER_TYPE.PASSENGER : USER_TYPE.DRIVER,
        userName: user.fullName,
      });

      toast.success(
        `${isPassenger ? USER_TYPE.PASSENGER : USER_TYPE.DRIVER
        } account reactivated successfully!`,
        {
          position: "top-right",
          autoClose: 3000,
          transition: Bounce,
        }
      );

      if (onSuccess) onSuccess();
      onRequestClose();
    } catch (error) {
      console.error("Error reactivating account:", error);
      toast.error("Failed to reactivate account. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        transition: Bounce,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRefundRide = (ride) => {
    setRideToRefund(ride);
    setShowRefundConfirmation(true);
  };

  const confirmRefundRide = async () => {
    if (!rideToRefund) return;

    setShowRefundConfirmation(false);
    setRefundingRideId(rideToRefund?.id);

    console.log("rideToRefund id nisha", rideToRefund);

    try {
      // Step 1: Process the refund through the API
      const refundResponse = await fetch(REFUND_REQUEST, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refund_reason: "cancelled_due_to_user_deactivation",
          ride_id: rideToRefund.id,
        }),
      });

      if (!refundResponse.ok) {
        const errorText = await refundResponse.text();
        console.error("Refund request failed:", errorText);
        throw new Error(errorText || "Failed to process refund");
      }

      const refundData = await refundResponse.json();
      console.log("Refund API Response:", refundData);

      console.log("rideToRefund source:", rideToRefund?.source);
      console.log("rideToRefund id:", rideToRefund?.id);

      // Step 2: Move ride to history and delete from original location
      await moveRideToHistory(rideToRefund.source, rideToRefund.id);

      toast.success(
        `Ride cancelled and refunded successfully! Amount: $${parseFloat(
          rideToRefund.price
        ).toFixed(
          2
        )}. The ride has been moved to history. Refunds are typically processed within 5–7 business days.`,
        {
          position: "top-right",
          autoClose: 6000,
          transition: Bounce,
        }
      );

      // Refresh ride data
      await fetchRideData();
    } catch (error) {
      console.error("Error refunding ride:", error);
      toast.error(
        error.message || "Failed to process refund. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        }
      );
    } finally {
      setRefundingRideId(null);
      setRideToRefund(null);
    }
  };

  const moveRideToHistory = async (source, rideId) => {
    try {
      let rideData = null;

      // Step 1: Fetch the ride data from the original location
      if (source === "firestore") {
        // Fetch from Firestore
        const rideRef = doc(db, COLLECTION_NAME.scheduleRides, rideId);
        const rideSnap = await getDoc(rideRef);

        if (rideSnap.exists()) {
          rideData = { id: rideSnap.id, ...rideSnap.data() };
        } else {
          throw new Error("Ride not found in Firestore");
        }
      } else {
        // Fetch from Realtime Database - search by ride.id field
        const database = getDatabase();
        const ridesRef = ref(database, COLLECTION_NAME.rides);

        rideData = await new Promise((resolve, reject) => {
          onValue(
            ridesRef,
            (snapshot) => {
              const ridesData = snapshot.val();
              if (ridesData) {
                // Find the ride where ride.id matches our rideId
                const dbKey = Object.keys(ridesData).find(
                  (key) => ridesData[key].id === rideId
                );

                if (dbKey) {
                  const ride = ridesData[dbKey];
                  resolve({ id: rideId, ...ride });
                  console.log(
                    `Found ride in Realtime Database. Ride ID: ${rideId}, DB Key: ${dbKey}`
                  );
                } else {
                  reject(
                    new Error(
                      `Ride with ID ${rideId} not found in Realtime Database`
                    )
                  );
                }
              } else {
                reject(new Error("No rides data in Realtime Database"));
              }
            },
            { onlyOnce: true }
          );
        });
      }

      if (!rideData) {
        throw new Error("Failed to fetch ride data");
      }

      // Step 2: Add cancellation information to the ride data
      const historyRideData = {
        ...rideData,
        id: rideId, // Ensure the id field is set
        status: "cancelled",
        cancellationStatus: "cancelled_due_to_user_deactivation",
        cancelledAt: new Date(),
        refundProcessed: true,
        movedToHistoryAt: new Date(),
        // Set pricing fields for refunded ride
        driverPrice: 0,
        swyftPrice: 0,
        originalPrice: rideData?.price,
        refundAmount: rideData?.price,
        price: 0,
      };

      // Step 3: Save to Staging Rides History (use rideId as document ID)
      const historyRef = doc(db, COLLECTION_NAME.ridesHistory, rideId);
      await setDoc(historyRef, historyRideData);
      console.log("Successfully moved ride to Staging Rides History:", rideId);

      // Step 4: Delete from original location
      if (source === "firestore") {
        // For Firestore, document ID matches ride ID
        const rideRef = doc(db, COLLECTION_NAME.scheduleRides, rideId);
        await deleteDoc(rideRef);
        console.log("Deleted ride from Firestore:", rideId);
      } else {
        // For Realtime Database, search by ride.id field to find the correct key
        const database = getDatabase();
        const ridesRef = ref(database, COLLECTION_NAME.rides);

        await new Promise((resolve, reject) => {
          onValue(
            ridesRef,
            async (snapshot) => {
              try {
                const ridesData = snapshot.val();
                if (ridesData) {
                  // Find the database key where ride.id matches our rideId
                  const dbKey = Object.keys(ridesData).find(
                    (key) => ridesData[key].id === rideId
                  );

                  if (dbKey) {
                    const rideRef = ref(
                      database,
                      `${COLLECTION_NAME.rides}/${dbKey}`
                    );
                    await remove(rideRef);
                    console.log(
                      `Deleted ride from Realtime Database. Ride ID: ${rideId}, DB Key: ${dbKey}`
                    );
                    resolve(true);
                  } else {
                    console.warn(
                      `Ride with ID ${rideId} not found in Realtime Database for deletion`
                    );
                    resolve(false);
                  }
                } else {
                  console.warn("No rides data in Realtime Database");
                  resolve(false);
                }
              } catch (error) {
                reject(error);
              }
            },
            { onlyOnce: true }
          );
        });
      }

      return true;
    } catch (err) {
      console.error("Error while moving ride to history:", err);
      throw err;
    }
  };

  const cancelRefund = () => {
    setShowRefundConfirmation(false);
    setRideToRefund(null);
  };

  const openAssignDriverModal = (ride) => {
    setSelectedRideForAssignment(ride);
    setIsAssignDriverModalOpen(true);
  };

  const closeAssignDriverModal = () => {
    setSelectedRideForAssignment(null);
    setIsAssignDriverModalOpen(false);
  };

  const onAssignDriver = async (driver, ride) => {
    try {
      setIsAssigning(true);
      console.log("Assigning driver:", driver);
      console.log("Ride data:", ride);
      console.log("Ride source:", ride.source);

      if (ride.source === "firestore") {
        // Update in Firestore
        const rideRef = doc(db, COLLECTION_NAME.scheduleRides, ride.id);
        await updateDoc(rideRef, {
          riderId: driver.id,
          riderName: driver.fullName,
          status: ride?.status,
        });
        console.log("Firestore update successful");
      } else {
        // Update in Realtime Database
        const database = getDatabase();
        const ridesRef = ref(database, COLLECTION_NAME.rides);

        // Find the database key
        await new Promise((resolve, reject) => {
          onValue(
            ridesRef,
            async (snapshot) => {
              try {
                const ridesData = snapshot.val();
                if (ridesData) {
                  const dbKey = Object.keys(ridesData).find(
                    (key) => ridesData[key].id === ride.id
                  );

                  if (dbKey) {
                    const rideRef = ref(
                      database,
                      `${COLLECTION_NAME.rides}/${dbKey}`
                    );
                    await update(rideRef, {
                      riderId: driver.id,
                      riderName: driver.fullName,
                      socketStatus: ride?.socketStatus,
                      // ride?.isScheduled && ride?.status === "pending"
                      //   ? "rideCreated"
                      //   : "driverPickup",
                      status: ride?.status,
                      // ride?.isScheduled && ride?.status === "pending"
                      //   ? "scheduled"
                      //   : "accepted",
                    });
                    console.log("Realtime Database update successful");
                    resolve(true);
                  } else {
                    reject(new Error("Ride not found in Realtime Database"));
                  }
                } else {
                  reject(new Error("No rides data in Realtime Database"));
                }
              } catch (error) {
                reject(error);
              }
            },
            { onlyOnce: true }
          );
        });
      }

      toast.success(
        `Driver ${driver.fullName} assigned to ride successfully!`,
        {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        }
      );

      closeAssignDriverModal();
      // Refresh ride data
      await fetchRideData();
    } catch (error) {
      console.error("Error assigning driver:", error);
      toast.error("Failed to assign driver. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel={isDeactivated ? "Reactivate Account" : "Deactivate Account"}
      className="relative bg-white rounded-xl shadow-xl p-6 max-w-3xl mt-16 mx-auto max-h-[80%] overflow-y-auto custom-scrollbar"
      overlayClassName="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
    >
      <button
        onClick={onRequestClose}
        className="absolute top-4 right-4 text-red-500 hover:text-red-600"
        disabled={processing}
      >
        <IoMdCloseCircleOutline size={26} />
      </button>

      <div className="flex flex-col mb-6">
        <h2 className="text-dazzledBlue text-2xl font-bold mb-2">
          {isDeactivated ? "Reactivate Account" : "Deactivate Account"}
        </h2>
        <p className="text-gray-600">
          {isDeactivated
            ? `Reactivate ${user.fullName}'s account`
            : `Review the following information before deactivating ${user.fullName}'s account`}
        </p>
      </div>

      {loading ? (
        <div className="py-8">
          <Loader />
        </div>
      ) : isDeactivated ? (
        // Reactivation View
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              This account is currently deactivated. Click the button below to
              reactivate it.
            </p>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
              onClick={onRequestClose}
              disabled={processing}
            >
              Cancel
            </button>
            <button
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              onClick={handleReactivate}
              disabled={processing}
            >
              {processing ? (
                <>
                  <div className="w-5 h-5 border-3 border-t-white border-green-300 rounded-full animate-spin"></div>
                  Reactivating...
                </>
              ) : (
                "Reactivate Account"
              )}
            </button>
          </div>
        </div>
      ) : (
        // Deactivation View
        <div className="space-y-4">
          {/* Active Ride Block */}
          {activeRide && (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-5 shadow-md">
              <div className="flex items-start gap-3 mb-3">
                <div className="text-4xl">🚫</div>
                <div className="flex-1">
                  <h3 className="text-red-900 font-bold text-xl mb-1">
                    Cannot Deactivate - Active Ride in Progress
                  </h3>
                  <p className="text-red-700 text-base">
                    This {isPassenger ? "passenger" : "driver"} currently has an
                    active ride that must be completed before the account can be
                    deactivated.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-red-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                  <span className="text-gray-700 font-semibold text-sm uppercase tracking-wide">
                    Ride Details
                  </span>
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    IN PROGRESS
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 font-medium min-w-[140px]">
                      📍 Pickup Address:
                    </span>
                    <span className="text-gray-900 font-medium flex-1">
                      {formatLocation(activeRide.pickupAddressRaw)}
                    </span>
                  </div>

                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 font-medium min-w-[140px]">
                      🎯 Destination Address:
                    </span>
                    <span className="text-gray-900 font-medium flex-1">
                      {formatLocation(activeRide.destinationAddressRaw)}
                    </span>
                  </div>

                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 font-medium min-w-[140px]">
                      Ride ID:
                    </span>
                    <span className="text-gray-700 font-mono text-sm flex-1">
                      {activeRide.id}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-red-100 border border-red-300 rounded-lg p-3">
                <p className="text-red-800 text-sm font-medium">
                  ℹ️ Please wait for this ride to be completed or cancelled
                  before attempting to deactivate this account.
                </p>
              </div>
            </div>
          )}

          {/* Scheduled/Awaiting Rides */}
          {!activeRide && scheduledRides.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">⏰</span>
                <h3 className="text-yellow-900 font-bold text-lg">
                  Scheduled/Awaiting Ride
                  {scheduledRides.length !== 1 ? "s" : ""}
                </h3>
              </div>
              <p className="text-yellow-800 mb-3 text-sm">
                {isPassenger
                  ? "The following rides will be cancelled and the passenger will be refunded:"
                  : "The following rides will need to be manually reassigned to another driver:"}
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                {scheduledRides.map((ride, index) => (
                  <div
                    key={ride.id}
                    className="bg-white border border-yellow-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">
                          #{index + 1}
                        </span>
                        <span className="text-xs text-gray-500 uppercase font-medium">
                          {ride.status}
                        </span>
                      </div>
                      <span className="text-green-700 font-bold text-lg">
                        $
                        {parseFloat(
                          isDriver ? ride.driverPrice || ride.price : ride.price
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-gray-500 min-w-[140px]">
                          📍 Pickup Address:
                        </span>
                        <span className="text-gray-800 font-medium flex-1">
                          {formatLocation(ride.pickupAddressRaw)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-gray-500 min-w-[140px]">
                          🎯 Destination Address:
                        </span>
                        <span className="text-gray-800 font-medium flex-1">
                          {formatLocation(ride.destinationAddressRaw)}
                        </span>
                      </div>
                      {ride.isScheduled && ride.scheduledTime && (
                        <div className="flex items-start gap-2">
                          <span className="text-gray-500 min-w-[140px]">
                            ⏰ Scheduled Time:
                          </span>
                          <span className="text-gray-700 font-medium flex-1">
                            {new Date(ride.scheduledTime).toLocaleString(
                              "en-US",
                              {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <span className="text-gray-500 min-w-[140px]">
                          📅 Created At:
                        </span>
                        <span className="text-gray-600 flex-1">
                          {ride.createdAt
                            ? ride.createdAt.toLocaleString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })
                            : "-----"}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {isPassenger && (
                      <div className="mt-3 pt-3 border-t border-yellow-200">
                        <button
                          onClick={() => handleRefundRide(ride)}
                          disabled={refundingRideId === ride.id}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                        >
                          {refundingRideId === ride.id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-t-white border-blue-300 rounded-full animate-spin"></div>
                              Processing Refund...
                            </>
                          ) : (
                            <>💰 Refund ${parseFloat(ride.price).toFixed(2)}</>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Assign Driver Button - Only for drivers */}
                    {isDriver && (
                      <div className="mt-3 pt-3 border-t border-yellow-200">
                        <button
                          onClick={() => openAssignDriverModal(ride)}
                          className="w-full px-4 py-2 bg-celadon text-primary rounded-md hover:bg-celadon/90 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                        >
                          👤 Assign Another Driver
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Scheduled Rides */}
          {!activeRide && scheduledRides.length === 0 && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-4">
              <p className="text-green-800">
                ✓ No active or scheduled rides found. Safe to proceed with
                deactivation.
              </p>
            </div>
          )}

          {/* Refund Total for Passengers */}
          {/* {!activeRide && isPassenger && refundTotal > 0 && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-blue-800 font-bold text-lg">
                  Total Refund Amount:
                </span>
                <span className="text-blue-900 font-bold text-2xl">
                  ${refundTotal.toFixed(2)}
                </span>
              </div>
              <p className="text-blue-700 text-sm mt-2">
                This amount will be refunded to the passenger for cancelled
                scheduled rides.
              </p>
            </div>
          )} */}

          {/* Driver Reassignment Note */}
          {/* {!activeRide && isDriver && scheduledRides.length > 0 && (
            <div className="bg-purple-50 border border-purple-300 rounded-lg p-4">
              <h3 className="text-purple-800 font-bold mb-2">
                Admin Action Required
              </h3>
              <p className="text-purple-700">
                After deactivation, you will need to manually reassign the{" "}
                {scheduledRides.length} scheduled ride
                {scheduledRides.length > 1 ? "s" : ""} to another driver.
              </p>
            </div>
          )} */}

          {/* Acknowledge Checkbox */}
          {!activeRide && scheduledRides.length === 0 && (
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1 w-5 h-5 text-primary focus:ring-primary border-gray-300 rounded"
                  disabled={processing}
                />
                <span className="text-gray-700">
                  I acknowledge that this action will deactivate the{" "}
                  {isPassenger ? "passenger" : "driver"}'s account. The user
                  will be notified of this action.
                </span>
              </label>
            </div>
          )}

          {/* Warning when scheduled rides exist */}
          {!activeRide && scheduledRides.length > 0 && (
            <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-2xl">⚠️</span>
                <div>
                  <h3 className="text-orange-900 font-bold text-base mb-1">
                    Action Required Before Deactivation
                  </h3>
                  <p className="text-orange-800 text-sm">
                    {isPassenger ? (
                      <>
                        Please refund all rides before deactivating this
                        passenger's account. Use the "Refund" button on each
                        ride above.
                      </>
                    ) : (
                      <>
                        This driver has pending/scheduled rides
                        {scheduledRides.length !== 1 ? "s" : ""} that must be
                        manually reassigned to another driver before
                        deactivation.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
              onClick={onRequestClose}
              disabled={processing}
            >
              Cancel
            </button>
            {!activeRide && (
              <button
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={handleDeactivate}
                disabled={
                  processing || !acknowledged || scheduledRides.length > 0
                }
                title={
                  scheduledRides.length > 0
                    ? "Please refund all scheduled rides before deactivating"
                    : !acknowledged
                      ? "Please acknowledge the deactivation"
                      : ""
                }
              >
                {processing ? (
                  <>
                    <div className="w-5 h-5 border-3 border-t-white border-red-300 rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  "Deactivate Account"
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Refund Confirmation Dialog */}
      {showRefundConfirmation && rideToRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 animate-fadeIn">
            <div className="flex items-start gap-3 mb-4">
              <div className="text-4xl">⚠️</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Cancel Ride & Process Refund
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Are you sure you want to cancel this ride and process a refund
                  of{" "}
                  <span className="font-bold text-green-700">
                    ${parseFloat(rideToRefund.price).toFixed(2)}
                  </span>
                  ?
                </p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center pb-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium">
                    Refund Amount:
                  </span>
                  <span className="font-bold text-green-700 text-lg">
                    ${parseFloat(rideToRefund.price).toFixed(2)}
                  </span>
                </div>
                <div className="pt-2">
                  <p className="text-gray-500 text-xs mb-1">
                    <strong>📍 Pickup:</strong>{" "}
                    {formatLocation(rideToRefund.pickupAddressRaw)}
                  </p>
                  <p className="text-gray-500 text-xs">
                    <strong>🎯 Destination:</strong>{" "}
                    {formatLocation(rideToRefund.destinationAddressRaw)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-blue-800 text-xs">
                ℹ️ The passenger will receive a 100% refund. Refunds are
                typically processed within 5–7 business days.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelRefund}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
              >
                No, Keep Ride
              </button>
              <button
                onClick={confirmRefundRide}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
              >
                Yes, Refund Ride
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      <AssignDriverModal
        isOpen={isAssignDriverModalOpen}
        onRequestClose={closeAssignDriverModal}
        ride={selectedRideForAssignment}
        onVerify={onAssignDriver}
        isAssigning={isAssigning}
      />
    </Modal>
  );
};

export default DeactivateAccountModal;

